import { SKINS, ZONE_NAMES } from '@shared/protocol';
import { api, ApiError, Profile, WorldInfo } from '../net/api';

declare const __CLIENT_BUILD_DATETIME__: string;

/**
 * Pre-game flow on the home page: redeem the invite from the URL, complete
 * the profile (nickname + skin), then pick one of the worlds.
 * Resolves with the chosen world id once the user is ready to play.
 */
export async function runLobby(): Promise<{ profile: Profile; worldId: number }> {
  const root = document.getElementById('lobby')!;
  const companyLogo = document.getElementById('company-logo') as HTMLImageElement;
  companyLogo.classList.add('visible');
  root.classList.remove('hidden');

  // Invite link: /?invite=TOKEN
  const params = new URLSearchParams(location.search);
  const urlInvite = params.get('invite');
  let inviteError = '';
  if (urlInvite) {
    try {
      await api.redeemInvite(urlInvite);
    } catch (e) {
      inviteError =
        e instanceof ApiError ? e.message : 'That invite link is not valid.';
    }
    history.replaceState(null, '', location.pathname);
  }

  let profile = await api.me();
  if (!profile) {
    // No session yet: let the player paste an invite code (or full link).
    await inviteScreen(root, inviteError);
    profile = await api.me();
    if (!profile) return new Promise(() => {}); // should not happen
  }

  if (!profile.nick) {
    profile = await profileScreen(root, profile);
  }

  // Worlds screen loops back here when the user opens the profile editor.
  while (true) {
    const choice = await worldsScreen(root, profile);
    if (choice.type === 'profile') {
      profile = await profileScreen(root, profile);
      continue;
    }
    root.classList.add('hidden');
    companyLogo.classList.remove('visible');
    return { profile, worldId: choice.worldId };
  }
}

/** Invite gate: redeem a code (or pasted invite link) to get a session. */
function inviteScreen(root: HTMLElement, initialError = ''): Promise<void> {
  return new Promise((resolve) => {
    const panel = renderPanel(
      root,
      `
      <div class="blocked">
        <h2>Invite required</h2>
        <p>BlankForge is invite-only for now.<br/>Enter your invite code to join.</p>
      </div>
      <input type="text" id="invite" placeholder="Invite code or link"
             autocomplete="off" autocapitalize="off" spellcheck="false" />
      <div class="error"></div>
      <button id="redeem">Enter</button>
    `,
    );

    const input = panel.querySelector<HTMLInputElement>('#invite')!;
    const errorBox = panel.querySelector<HTMLElement>('.error')!;
    const button = panel.querySelector<HTMLButtonElement>('#redeem')!;
    errorBox.textContent = initialError;

    const submit = async () => {
      const token = parseInviteToken(input.value);
      if (!token) {
        errorBox.textContent = 'Enter an invite code';
        return;
      }
      button.disabled = true;
      errorBox.textContent = '';
      try {
        await api.redeemInvite(token);
        resolve();
      } catch (e) {
        errorBox.textContent =
          e instanceof ApiError ? e.message : 'Failed to redeem invite';
        button.disabled = false;
      }
    };

    button.onclick = submit;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void submit();
    });
    input.focus();
  });
}

/** Accepts a bare token or a full invite link (e.g. https://.../?invite=TOKEN). */
function parseInviteToken(raw: string): string {
  const trimmed = raw.trim();
  const match = /[?&]invite=([^&\s]+)/.exec(trimmed);
  return match ? decodeURIComponent(match[1]) : trimmed;
}

function renderPanel(root: HTMLElement, inner: string): HTMLElement {
  root.innerHTML = `<div class="panel">
    <h1>BLANK<span>FORGE</span></h1>
    <div class="sub">multiplayer sandbox — build anything</div>
    ${inner}
  </div>`;
  return root.querySelector('.panel')!;
}

/** Nickname + skin selection. Editable again later via "Edit profile". */
function profileScreen(root: HTMLElement, profile: Profile): Promise<Profile> {
  const firstTime = !profile.nick;
  return new Promise((resolve) => {
    const panel = renderPanel(root, `
      <h2>${firstTime ? 'Set up your character' : 'Edit profile'}</h2>
      <input type="text" id="nick" maxlength="20" placeholder="Nickname (2-20 characters)" />
      <h2>Choose a skin</h2>
      <div class="skins"></div>
      <div class="error"></div>
      <button id="save">Save and continue</button>
      ${firstTime ? '' : '<button id="cancel" class="secondary">Cancel</button>'}
    `);

    panel.querySelector<HTMLButtonElement>('#cancel')?.addEventListener('click', () =>
      resolve(profile),
    );

    const skinsBox = panel.querySelector('.skins')!;
    let selectedSkin = profile.skin;
    for (const skin of SKINS) {
      const el = document.createElement('div');
      el.className = 'skin' + (skin.id === selectedSkin ? ' selected' : '');
      el.innerHTML = `<canvas width="8" height="8"></canvas><div class="name">${skin.name}</div>`;
      const ctx = el.querySelector('canvas')!.getContext('2d')!;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 8, 8, 8, 8, 0, 0, 8, 8); // face region
      img.src = `/skins/${skin.id}.png`;
      el.onclick = () => {
        selectedSkin = skin.id;
        skinsBox.querySelectorAll('.skin').forEach((s) => s.classList.remove('selected'));
        el.classList.add('selected');
      };
      skinsBox.appendChild(el);
    }

    const nickInput = panel.querySelector<HTMLInputElement>('#nick')!;
    nickInput.value = profile.nick ?? '';
    const errorBox = panel.querySelector<HTMLElement>('.error')!;
    const save = panel.querySelector<HTMLButtonElement>('#save')!;

    save.onclick = async () => {
      save.disabled = true;
      errorBox.textContent = '';
      try {
        await api.saveProfile(nickInput.value.trim(), selectedSkin);
        resolve({ ...profile, nick: nickInput.value.trim(), skin: selectedSkin });
      } catch (e) {
        errorBox.textContent = e instanceof ApiError ? e.message : 'Failed to save';
        save.disabled = false;
      }
    };
  });
}

type WorldsChoice = { type: 'world'; worldId: number } | { type: 'profile' };

/** World list with zone occupancy, plus the profile editor entry. */
async function worldsScreen(root: HTMLElement, profile: Profile): Promise<WorldsChoice> {
  const [worlds, health] = await Promise.all([
    api.worlds(),
    api.health().catch(() => null),
  ]);
  return new Promise((resolve) => {
    root.innerHTML = `
      <div class="lobby-dashboard">
        <header class="dashboard-head">
          <h1>BLANK<span>FORGE</span></h1>
          <div class="sub">Choose a world and start building</div>
        </header>
        <div class="dashboard-layout">
          <main class="worlds"></main>
          <div class="profile-column">
            <aside class="profile-card">
              <div class="profile-title">
                <div class="profile-badge">${escapeHtml((profile.nick ?? '?').charAt(0).toUpperCase())}</div>
                <div>
                  <div class="profile-name">${escapeHtml(profile.nick ?? '')}</div>
                  <div class="profile-role">Builder</div>
                </div>
              </div>
              <div class="profile-avatar"><canvas width="8" height="8"></canvas></div>
              <button id="edit-profile">Customize profile</button>
            </aside>
            <div class="build-info">
              <div>Client build: ${escapeHtml(formatBuildDatetime(__CLIENT_BUILD_DATETIME__))}</div>
              <div>Backend build: ${escapeHtml(formatBuildDatetime(health?.buildDatetime))}</div>
            </div>
          </div>
        </div>
      </div>`;

    const avatar = root.querySelector<HTMLCanvasElement>('.profile-avatar canvas')!;
    drawSkinFace(avatar, profile.skin);

    const box = root.querySelector('.worlds')!;
    for (const world of worlds) {
      const el = document.createElement('div');
      el.className = 'world';
      el.innerHTML = `
        <div class="world-art" aria-hidden="true"></div>
        <div class="world-body">
          <div class="wname">${escapeHtml(world.name)}</div>
          <div class="zones">${zonesSummary(world, profile.id)}</div>
          <button type="button">Enter world</button>
        </div>`;
      el.querySelector('button')!.onclick = () =>
        resolve({ type: 'world', worldId: world.id });
      box.appendChild(el);
    }
    root.querySelector<HTMLButtonElement>('#edit-profile')!.onclick = () =>
      resolve({ type: 'profile' });
  });
}

function formatBuildDatetime(value: string | undefined): string {
  if (!value) return 'unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function drawSkinFace(canvas: HTMLCanvasElement, skin: string): void {
  const ctx = canvas.getContext('2d')!;
  const img = new Image();
  img.onload = () => ctx.drawImage(img, 8, 8, 8, 8, 0, 0, 8, 8);
  img.src = `/skins/${skin}.png`;
}

function zonesSummary(world: WorldInfo, selfId: string): string {
  return world.zones
    .map((z) => {
      const owner =
        z.ownerId === null
          ? 'free'
          : z.ownerId === selfId
            ? 'you'
            : escapeHtml(z.ownerNick ?? '?');
      return `${ZONE_NAMES[z.index]}: ${owner}`;
    })
    .join('<br/>');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
